import { type CSSProperties, memo, type ReactNode, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import { useIsomorphicLayoutEffect } from 'react-use';

import { SubSideNavResizer } from '~/components/v2/components/workspace/sub-side-nav/SubSideNavResizer';
import { useSubSideNavStore } from '~/components/v2/store/features/workspace/useSubSideNavStore';
import { CURRENT_WORKSPACE_NAV_WIDTH } from '~/constants/sideNav';

const SUB_SIDE_NAV_TOGGLE_HOTKEYS = 'meta+\\,ctrl+\\,w';

export type SubSideNavProps = {
  active: boolean;
  children: ReactNode;
  collapsible: boolean;
  label: string;
  style?: CSSProperties;
};

export const SubSideNav = memo(({ active, children, collapsible, label, style }: SubSideNavProps) => {
  const storeOpen = useSubSideNavStore((state) => state.open);
  const toggleOpen = useSubSideNavStore((state) => state.toggleOpen);
  const open = active && (collapsible ? storeOpen : true);
  const navRef = useRef<HTMLElement>(null);

  useHotkeys(
    SUB_SIDE_NAV_TOGGLE_HOTKEYS,
    () => {
      toggleOpen();
    },
    {
      enabled: active && collapsible,
      preventDefault: true,
    },
    [active, collapsible, toggleOpen],
  );

  useIsomorphicLayoutEffect(() => {
    const navElement = navRef.current;

    if (navElement !== null) {
      navElement.inert = !open;
    }
  }, [open]);

  return (
    <div
      className="relative z-30 h-full min-w-0 shrink-0 overflow-hidden"
      data-state={open ? 'open' : 'closed'}
      data-testid="SUB_SIDE_NAV_PANEL"
      style={style}
    >
      {/* Inner width is --sidebar-content-width (not --sidebar-width) so labels stay full-size and clip as the panel closes. */}
      <div className="h-full overflow-hidden ads:border-default/80 border-gray-4/80 border-r ads:bg-base bg-gray-1">
        {/* pr-3 matches SidebarResizer's w-3 hit target. It has to sit inside the border box so always-visible scrollbars land beside the handle instead of under it. */}
        <nav
          aria-hidden={!open}
          aria-label={label}
          className="box-border flex h-full min-h-0 shrink-0 flex-col pr-3"
          data-testid="SUB_SIDE_NAV"
          ref={navRef}
          style={{ width: `var(--sidebar-content-width, ${CURRENT_WORKSPACE_NAV_WIDTH}px)` }}
        >
          {children}
        </nav>
      </div>
      {open ? <SubSideNavResizer collapsible={collapsible} /> : null}
    </div>
  );
});

SubSideNav.displayName = 'SubSideNav';
